import React, { useEffect, useState } from 'react';
import { paymentsApi } from '../services/paymentsApi';
import { CheckCircle2, XCircle, Loader2, ArrowRight } from 'lucide-react';

const PaymentStatus = ({ onContinue }) => {
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    checkPaymentStatus();
  }, []);

  const checkPaymentStatus = async () => {
    // Get session_id from URL
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (!sessionId) {
      setStatus('failed');
      return;
    }

    // Poll for payment status
    let attempts = 0;
    const maxAttempts = 10;
    const pollInterval = 2000;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setStatus('failed');
        return;
      }

      try {
        const result = await paymentsApi.getCheckoutStatus(sessionId);
        setPaymentData(result);

        if (result.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (result.status === 'expired') {
          setStatus('failed');
          return;
        }

        // Continue polling
        attempts++;
        setTimeout(poll, pollInterval);
      } catch (error) {
        console.error('Error checking status:', error);
        attempts++;
        setTimeout(poll, pollInterval);
      }
    };

    poll();
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900 flex items-center justify-center">
      <div className="max-w-md w-full mx-4 p-8 bg-slate-800 rounded-2xl border border-slate-700 text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-indigo-500 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Processing Payment</h2>
            <p className="text-slate-400">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-12 h-12 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
            <p className="text-slate-400 mb-6">
              Thank you for your purchase! Your credits have been added to your account.
            </p>
            {paymentData && (
              <div className="mb-6 p-4 bg-slate-900 rounded-lg">
                <p className="text-sm text-slate-400">Amount Paid</p>
                <p className="text-2xl font-bold text-white">
                  ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                </p>
              </div>
            )}
            <button
              onClick={onContinue}
              className="w-full py-3 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white font-bold rounded-xl flex items-center justify-center gap-2"
            >
              Start Creating Content <ArrowRight className="w-5 h-5" />
            </button>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-12 h-12 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Payment Failed</h2>
            <p className="text-slate-400 mb-6">
              Something went wrong with your payment. Please try again or contact support.
            </p>
            <button
              onClick={onContinue}
              className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-xl"
            >
              Go Back
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentStatus;
